import { FC, useMemo } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import { Alert, Button, Paper, Stack, Typography } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { LogLevel, useLogStatus } from "src/utils/newRelic/useLogStatus";
import { useFetchEmployee } from "src/Pages/Company/Absences/api/useEmployee";
import {
  useAcceptAbsenceRequests,
  useFetchAbsenceRequestHistory,
  useFetchEmployeeAbsenceRequest,
} from "src/Pages/Company/Absences/api/useAbsenceRequest";
import { useConfirmRejection } from "src/utils/hooks/absences/useConfirmRejection";
import { useRequireCorrection } from "src/utils/hooks/absences/useRequireCorrection";
import __ from "src/utils/Translations";
import useBasePath from "src/utils/hooks/useBasePath";
import formatDateRangePreview, {
  formatHourRangePreview,
} from "src/utils/Company/Absences/formatDateRangePreview";
import Title from "src/Pages/Company/Absences/Title/Title";
import BreadcrumbsMui from "src/Components/BreadcrumbsMui/BreadcrumbsMui";
import { Loader } from "src/Components/Loader/Loader";
import ErrorDataFetching from "src/Components/Error/ErrorDataFetching";
import Fieldset from "src/Pages/Company/Absences/Forms/Fieldset";
import FileList from "src/Pages/Company/Absences/Forms/FileList/FileList";
import { StatusChip } from "src/Pages/Company/Absences/AbsenceRequest/StatusChip";
import EmployeeCardSkeleton from "src/Pages/Company/Absences/EmployeeCard/EmployeeCardSkeleton";
import EmployeeCard from "src/Pages/Company/Absences/EmployeeCard";
import EmployeeDetails from "src/Pages/Company/Absences/EmployeeDetails";
import AbsenceRequestHistory from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequestHistory";
import AbsenceRequestCustomFieldsPreview from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequestCustomFieldsPreview";
import { EPatternUnit } from "src/Pages/Company/Absences/AbsenceType/AbsenceType.models";

const AbsenceRequestPreview: FC = () => {
  const { absenceRequestId } = useParams<Record<string, string>>();
  const history = useHistory();
  const basePath = useBasePath();
  const logStatus = useLogStatus();

  if (!absenceRequestId) {
    logStatus({
      level: LogLevel.DEBUG,
      message: "No Absence Request ID provided.",
      file: "AbsenceRequestPreview.tsx",
    });
  }

  const {
    data: absenceRequest,
    isFetching: isFetchingAbsenceRequest,
    isError: isErrorAbsenceRequest,
  } = useFetchEmployeeAbsenceRequest(absenceRequestId!);

  const employeeId = useMemo(() => {
    if (!absenceRequest) return undefined;

    return absenceRequest.employee.employeeId;
  }, [absenceRequest]);

  const {
    data: employee,
    isFetching: isFetchingEmployee,
    isError: isErrorEmployee,
  } = useFetchEmployee(employeeId);

  const {
    data: absenceRequestHistory,
    isFetching: isFetchingAbsenceRequestHistory,
    isError: isErrorAbsenceRequestHistory,
  } = useFetchAbsenceRequestHistory(absenceRequestId!);

  const { mutate: acceptAbsenceRequests, isPending: isPendingAcceptMutation } =
    useAcceptAbsenceRequests();

  const { confirmRejection } = useConfirmRejection();

  const { requireCorrection } = useRequireCorrection();

  if (!isFetchingEmployee && isFetchingAbsenceRequest) return <Loader />;

  if (isErrorEmployee || isErrorAbsenceRequest) return <ErrorDataFetching />;

  if (!absenceRequest || !employee) return null;

  return (
    <>
      <BreadcrumbsMui
        items={[
          {
            label: __("Nieobecności"),
            to: `${basePath}/absences`,
          },
          {
            label: __("Kartoteki pracowników"),
            to: `${basePath}/absences/absence-records`,
          },
          {
            label: __("Lista wniosków"),
            to: `${basePath}/absences/employee/${employeeId}#requests`,
          },
          {
            label: __(
              `${__("Podgląd wniosku")} ${absenceRequest.absenceType.name}`
            ),
          },
        ]}
      />
      <Title
        title={`${__("Podgląd wniosku")} ${absenceRequest.absenceType.name}`}
      />
      {isFetchingEmployee ? (
        <EmployeeCardSkeleton />
      ) : (
        <EmployeeCard data={employee} />
      )}
      <Paper
        sx={{ p: "2rem", mt: "1rem" }}
        data-testid="absence-request-preview"
      >
        <Fieldset
          legend={__("Status")}
          data-testid="absence-request-preview__status"
        >
          <StatusChip variant={absenceRequest.status} />
        </Fieldset>
        <Fieldset legend={__("Rodzaj wniosku")}>
          <Typography
            variant="body1"
            data-testid="absence-request-preview__name"
          >
            {absenceRequest.absenceType.name}
          </Typography>
        </Fieldset>
        {absenceRequest.absenceType.description && (
          <Fieldset legend={__("Opis")}>
            <Typography
              variant="body1"
              data-testid="absence-request-preview__description"
            >
              {absenceRequest.absenceType.description}
            </Typography>
          </Fieldset>
        )}
        <Fieldset legend={__("Termin wniosku")}>
          {absenceRequest.type === EPatternUnit.Hour && (
            <>
              <Typography variant="body1" component="span">
                {__("Tryb wniosku")}:
              </Typography>{" "}
              <Typography variant="subtitle1" component="span">
                {__("godzinowy")}
              </Typography>
            </>
          )}
          <Typography
            variant="body1"
            data-testid="absence-request-preview__times"
          >
            {absenceRequest.type === EPatternUnit.Day
              ? formatDateRangePreview(absenceRequest.absenceTimes)
              : formatHourRangePreview(absenceRequest.absenceTimes)}
          </Typography>
          {absenceRequest.type === EPatternUnit.Day && (
            <>
              <Typography
                variant="body1"
                data-testid="absence-request-preview__total-day-count"
              >
                {__("Liczba dni kalendarzowych")}:{" "}
                <strong>{absenceRequest.totalTimeAmount.days}</strong>
              </Typography>
              <Typography
                variant="body1"
                data-testid="absence-request-preview__effective-day-count"
              >
                {__("Liczba dni roboczych")}:{" "}
                <strong>{absenceRequest.effectiveTimeAmount.days}</strong>
              </Typography>
            </>
          )}
          <Typography
            variant="body1"
            data-testid="absence-request-preview__hour-count"
          >
            {__("Liczba godzin")}:{" "}
            <strong>{absenceRequest.effectiveTimeAmount.hours}</strong>
          </Typography>
        </Fieldset>
        <Fieldset legend={__("Uprawnienia i zastępstwo")}>
          <Typography variant="body1">
            {__("Imię i nazwisko osoby akceptującej wniosek")}:
          </Typography>
          <Typography
            variant="subtitle1"
            data-testid="absence-request-preview__approver"
          >
            <EmployeeDetails data={absenceRequest.approver} showFk />
          </Typography>
          <Typography variant="body1">
            {__("Imię i nazwisko osoby wyznaczonej na zastępstwo")}:
          </Typography>
          <Typography
            variant="subtitle1"
            data-testid="absence-request-preview__replacer"
          >
            {absenceRequest.replacer ? (
              <EmployeeDetails data={absenceRequest.replacer} showFk />
            ) : (
              "-"
            )}
          </Typography>
        </Fieldset>
        {absenceRequest.justification && (
          <Fieldset legend={__("Uzasadnienie")}>
            <Typography
              variant="body1"
              data-testid="absence-request-preview__justification"
            >
              {absenceRequest.justification}
            </Typography>
          </Fieldset>
        )}
        {absenceRequest.customFieldValues && (
          <AbsenceRequestCustomFieldsPreview
            customFieldsConfiguration={
              absenceRequest.absenceType.customFieldsConfiguration
            }
            customFieldValues={absenceRequest.customFieldValues}
          />
        )}
        {absenceRequest.comment && (
          <Fieldset legend={__("Komentarz")}>
            <Typography
              variant="body1"
              data-testid="absence-request-preview__comment"
            >
              {absenceRequest.comment}
            </Typography>
          </Fieldset>
        )}
        {absenceRequest.attachments?.length ? (
          <FileList
            scopeId={absenceRequestId!}
            title={__("Załączniki")}
            data={absenceRequest.attachments}
            downloadLabel={__("Pobierz")}
            data-testid="absence-request-preview__attachments"
          />
        ) : null}
        <Stack direction="row" spacing={2} mt="3rem">
          {absenceRequest.actionsAllowed.rejectable && (
            <Button
              variant="contained"
              color="error"
              size="large"
              onClick={() =>
                confirmRejection({
                  absenceRequestId,
                  absenceTypeName: absenceRequest.absenceType.name,
                  employeeFirstName: employee.firstName,
                  employeeLastName: employee.lastName,
                })
              }
              data-testid="absence-request-preview__reject-button"
            >
              {__("Odrzuć wniosek")}
            </Button>
          )}
          <Button
            variant="outlined"
            size="large"
            onClick={() => history.goBack()}
            sx={{ marginLeft: "auto !important" }}
          >
            {__("Wróć")}
          </Button>
          {absenceRequest.actionsAllowed.sendForCorrectionEligible && (
            <Button
              variant="contained"
              size="large"
              startIcon={<EditIcon />}
              onClick={() => requireCorrection(absenceRequestId)}
              data-testid="absence-request-preview__send-for-correction-button"
            >
              {__("Zgłoś do poprawy")}
            </Button>
          )}
          {absenceRequest.actionsAllowed.editable && (
            <Button
              variant="contained"
              size="large"
              component={Link}
              to={`${basePath}/absences/absence-request/${absenceRequestId}/edit/${employeeId}`}
              data-testid="absence-request-preview__edit-button"
            >
              {__("Edytuj wniosek")}
            </Button>
          )}
          {absenceRequest.actionsAllowed.approvable && (
            <Button
              variant="contained"
              size="large"
              onClick={() => acceptAbsenceRequests([absenceRequestId])}
              loading={isPendingAcceptMutation}
              data-testid="absence-request-preview__approve-button"
            >
              {__("Akceptuj wniosek")}
            </Button>
          )}
        </Stack>
      </Paper>
      <Paper
        sx={{ padding: "1rem", mt: "2rem" }}
        data-testid="absence-request-history"
      >
        {/* eslint-disable-next-line no-nested-ternary */}
        {isErrorAbsenceRequestHistory ? (
          <Alert severity="error">
            {__("Błąd pobierania danych historii wniosku.")}
          </Alert>
        ) : isFetchingAbsenceRequestHistory ? (
          <Loader />
        ) : (
          absenceRequestHistory && (
            <AbsenceRequestHistory
              data={absenceRequestHistory}
              billingCycle={absenceRequest.type}
            />
          )
        )}
      </Paper>
    </>
  );
};

export default AbsenceRequestPreview;
